import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormControl,
  Validators,
  AbstractControl,
  ValidatorFn,
} from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { take } from 'rxjs';

import { Store } from '@ngrx/store';
import { AppState } from '@state/app.state';
import {
  registerPasswordlessAction,
  setAuthDefaultValues,
  setAuthModal,
  signUpActionFailure,
} from '@state/auth/auth.actions';
import {
  selectAuthLoading,
  selectAuthErrors,
  selectReferrerCode,
  selectAuthDefaultValues,
} from '@state/auth/auth.selectors';

import { StringPipe } from '@shared/pipes';
import { AuthService } from '@core/services/auth.service';
import { IUserInviteSource } from '@shared/models/auth';
import { phoneNumberValidator } from '@core/constants/validators';

@UntilDestroy()
@Component({
  selector: 'app-signup-no-password',
  templateUrl: './no-password.component.html',
  styleUrls: ['./no-password.component.scss'],
})
export class SignUpNoPasswordComponent implements OnInit, OnDestroy {
  public loading = false;
  public focused = '';
  protected loadingInviteSources = false;
  protected inviteSources: IUserInviteSource[] = [];

  protected emailAccountDeactivated = false;
  protected emailAlreadyTaken = false;
  protected phoneNumberAccountDeactivated = false;
  protected phoneNumberAlreadyTaken = false;

  public signUpForm: FormGroup = this.formBuilder.group({
    firstName: new FormControl('', [Validators.required]),
    surname: new FormControl('', [Validators.required]),
    email: new FormControl('', [
      Validators.required,
      this.customEmailValidator(),
    ]),
    phoneNumber: new FormControl('', [
      Validators.required,
      phoneNumberValidator(),
    ]),
    terms: new FormControl(false, [Validators.requiredTrue]),
    allowMarketingEmails: new FormControl(false, [Validators.requiredTrue]),
    referrerCode: new FormControl(null),
    inviteSource: new FormControl('', [Validators.required]),
  });

  constructor(
    private formBuilder: FormBuilder,
    private store: Store<AppState>,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.signUpForm.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      if (this.focused) {
        this.signUpForm.controls[this.focused].markAsTouched();
      }
    });

    this.store
      .select(selectAuthDefaultValues)
      .pipe(take(1))
      .subscribe((values) => {
        this.signUpForm.patchValue({
          email: values?.email || '',
          phoneNumber: values?.phoneNumber || '',
        });

        this.store.dispatch(
          setAuthDefaultValues({
            email: undefined,
            phoneNumber: undefined,
          })
        );
      });

    this.store
      .select(selectAuthLoading)
      .pipe(untilDestroyed(this))
      .subscribe((loading) => {
        this.loading = loading;
        if (loading) {
          this.signUpForm.disable();
        } else {
          this.signUpForm.enable();
        }
      });

    this.signUpForm
      .get('email')
      ?.valueChanges.pipe(untilDestroyed(this))
      .subscribe(() => {
        this.emailAccountDeactivated = false;
        this.emailAlreadyTaken = false;
      });

    this.signUpForm
      .get('phoneNumber')
      ?.valueChanges.pipe(untilDestroyed(this))
      .subscribe(() => {
        this.phoneNumberAccountDeactivated = false;
        this.phoneNumberAlreadyTaken = false;
      });

    this.store
      .select(selectAuthErrors)
      .pipe(untilDestroyed(this))
      .subscribe((errors) => {
        if (errors && errors.length) {
          this.signUpForm.markAllAsTouched();
          errors.forEach((error) => {
            if (error.fields[0] === 'referrer_code') return;
            if (
              error.fields[0] === 'phone_number' &&
              error.shortMessage.includes('%{regex}')
            ) {
              this.signUpForm.controls[
                new StringPipe().transform(error.fields[0], 'toCamelCase')
              ].setErrors({
                server: 'Phone number is invalid',
              });
            } else {
              this.signUpForm.controls[
                new StringPipe().transform(error.fields[0], 'toCamelCase')
              ].setErrors({
                server: new StringPipe().transform(
                  error.shortMessage,
                  'capitalizeFirstLetter'
                ),
              });

              if (error.fields[0] === 'email') {
                if (error.message.includes('account is inactive')) {
                  this.emailAccountDeactivated = true;
                } else if (error.message.includes('already been taken')) {
                  this.emailAlreadyTaken = true;
                }
              } else if (error.fields[0] === 'phone_number') {
                if (error.message.includes('account is inactive')) {
                  this.phoneNumberAccountDeactivated = true;
                } else if (error.message.includes('already been taken')) {
                  this.phoneNumberAlreadyTaken = true;
                }
              }
            }
          });
        }
      });

    this.store
      .select(selectReferrerCode)
      .pipe(untilDestroyed(this))
      .subscribe((referrerCode) => {
        this.signUpForm.controls['referrerCode'].setValue(referrerCode || null);
      });

    this.authService
      .listUserInviteSources()
      .pipe(take(1))
      .subscribe((data) => {
        this.loadingInviteSources = false;
        this.inviteSources = data.listUserInviteSources;
      });
  }

  ngOnDestroy(): void {
    this.store.dispatch(signUpActionFailure({ errors: [] }));
  }

  submitHandler() {
    if (this.signUpForm.invalid) {
      this.signUpForm.markAllAsTouched();
      return;
    }
    try {
      const values = this.signUpForm.value;
      delete values.terms;
      this.store.dispatch(
        registerPasswordlessAction({
          input: { ...values, phoneNumber: '+1' + values.phoneNumber },
        })
      );
    } catch (err) {
      throw err;
    }
  }

  pastePhoneNumberHandler(event: ClipboardEvent) {
    const pastedText = event.clipboardData
      ?.getData('text/plain')
      .replace(/[\s()-]/g, '');
    setTimeout(() => {
      if (pastedText?.startsWith('+1')) {
        this.signUpForm.controls['phoneNumber'].setValue(
          pastedText?.substring(2, pastedText.length)
        );
      }
    }, 0);
  }

  focusHandler(event: FocusEvent) {
    const name = (event.target as HTMLInputElement).name;
    this.focused = name;
  }

  blurHandler(event: FocusEvent) {
    const name = (event.target as HTMLInputElement).name;
    if (this.focused === name) this.focused = '';
  }

  customEmailValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{1,4}$/;
      const valid = emailRegex.test(control.value);
      return valid ? null : { email: { value: control.value } };
    };
  }

  showSignInModal(type: 'email' | 'phone-number'): void {
    this.store.dispatch(
      setAuthModal({
        authModal: 'signin',
        defaultEmail:
          type === 'email' ? this.signUpForm.value.email : undefined,
        defaultPhoneNumber:
          type === 'phone-number'
            ? this.signUpForm.value.phoneNumber
            : undefined,
      })
    );
  }
}
